import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Plane, Edit, ArrowLeft, Save, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EditTravelRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    purpose: '',
    travel_type: 'flight',
    travel_category: 'Domestic',
    is_international: false,
    is_emergency: false,
    estimated_budget: 0,
    remarks: '',
  });

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/requests/${id}`, { withCredentials: true });
      const req = data.request;
      setFormData({
        purpose: req.purpose || '',
        travel_type: req.travel_type || 'flight',
        travel_category: req.travel_category || 'Domestic',
        is_international: !!req.is_international,
        is_emergency: !!req.is_emergency,
        estimated_budget: req.estimated_budget || 0,
        remarks: req.remarks || '',
      });
    } catch (err) {
      toast.error('Failed to load travel request');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.patch(`${API_URL}/travel-desk/requests/${id}`, formData, { withCredentials: true });
      toast.success('Travel request updated successfully!');
      navigate(`/travel/request/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update travel request');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-500 font-semibold">Loading request...</div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-[1000px] mx-auto font-sans">
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/travel/request/${id}`)} className="p-2 bg-white/10 rounded-xl hover:bg-white/20">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-black">Edit Travel Request</h1>
            <p className="text-xs text-blue-200">Modify travel request parameters before re-submission.</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <form onSubmit={handleSave} className="space-y-4 text-xs">
          <div>
            <label className="block font-bold text-slate-700 mb-1">Travel Purpose *</label>
            <input
              type="text"
              value={formData.purpose}
              onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-semibold outline-none"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-bold text-slate-700 mb-1">Travel Type</label>
              <select
                value={formData.travel_type}
                onChange={(e) => setFormData({ ...formData, travel_type: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-semibold"
              >
                <option value="flight">Flight</option>
                <option value="train">Train</option>
                <option value="bus">Bus</option>
                <option value="cab">Cab</option>
                <option value="hotel">Hotel</option>
              </select>
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Estimated Budget (₹)</label>
              <input
                type="number"
                value={formData.estimated_budget}
                onChange={(e) => setFormData({ ...formData, estimated_budget: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold"
              />
            </div>
          </div>

          <div className="pt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => navigate(`/travel/request/${id}`)}
              className="px-4 py-2 bg-slate-100 font-bold rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white font-extrabold rounded-xl shadow-md flex items-center gap-1.5"
            >
              <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTravelRequest;
